import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, switchMap } from 'rxjs';
import { LoadingService } from '@app/core/services';
import { AccountType } from '@app/enums';
import { IAccountSearchModel } from '@app/interfaces/lsb/account-search';
import { ParentAccountList } from '@app/modules/fund-management/accounts/models/accounts';
import { IListService } from '@app/services';
import { AccountService } from '@app/shared/services';
import { isNil } from '@app/utils';
import { AccountMassUpdateModel } from './account-mass-update.model';

@Injectable()
export class AccountMassUpdateService implements IListService {
	endpointBase: string;
	private readonly emptyAccountList = [{ Id: null, AccountNumber: 'No matching accounts' }];

	constructor(private readonly accountService: AccountService, private readonly loadingService: LoadingService) {}

	getList(): Observable<ParentAccountList> {
		// Retrieve all accounts the user has access to.
		return this.accountService.searchAccounts({ AccountNumber: '', Limit: 0, Filter: true } as IAccountSearchModel);
	}

	deleteEntity(): Observable<any> {
		return EMPTY;
	}

	saveAccountMassUpdate(model: AccountMassUpdateModel) {
		return this.accountService.accountMassUpdate(model);
	}

	getOffsetAccountsForSearchInput(value: string, fund: string, fundIndex: number): Observable<any> {
		const offsetFund = this.getFund(value, fundIndex);
		if (isNil(fund) || isNil(offsetFund) || fund !== offsetFund) {
			return of(this.emptyAccountList);
		}

		return this.getAccountsForSearchInput(value, []);
	}

	getSalesTaxAccountsForSearchInput(value: string): Observable<any> {
		const accountTypeIds = [AccountType.Expense, AccountType.Net];
		return this.getAccountsForSearchInput(value, accountTypeIds);
	}

	private getAccountsForSearchInput(value: string, accountTypeIds: number[]): Observable<any> {
		this.loadingService.skipNextRequest();
		return this.accountService
			.searchAccounts({
				AccountNumber: value,
				Limit: 50,
				AccountTypeIds: accountTypeIds,
				Filter: true, // filter for user's account access
				OnlyActive: true,
			} as IAccountSearchModel)
			.pipe(
				switchMap((data: any) => {
					if (data.isValid) {
						return of(
							data.accounts.map((item: any) => ({
								Id: item.Id,
								AccountNumber: item.AccountNumber,
								DescriptionValue: item.Description,
							}))
						);
					} else {
						return of(this.emptyAccountList);
					}
				})
			);
	}

	public getFund(account: string, index: number): string {
		const elements: string[] = account.split('.');
		return elements[index] || null;
	}
}
